import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Pencil, Plus, Trash2, CalendarDays, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EventFormDialog } from "@/components/EventFormDialog";
import { SessionCard } from "@/components/SessionCard";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EventTyreSets } from "@/components/EventTyreSets";
import { QuickImportDialog } from "@/components/QuickImportDialog";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { formatDate, lapToSeconds, sessionBestSeconds } from "@/lib/format";
import { useGarage } from "@/lib/useGarage";

const EventLists = ({ event }) => {
  const { t } = useT();
  const hideCat = useSettings().settings.hide_categories;
  const groups = [["categories", hideCat ? [] : event.categories, "text-[#FFB800] border-[#FFB800]/40"], ["drivers", event.drivers, "text-slate-200 border-[#282D3D]"]];
  return (
    <div className="mt-4 space-y-2">
      {groups.filter(([, list]) => list?.length).map(([key, list, cls]) => (
        <div key={key} data-testid={`event-detail-${key}`} className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 w-20">{t(key)}</span>
          {list.map((v) => <span key={v} className={`font-mono text-xs border rounded-sm px-2 py-0.5 ${cls}`}>{v}</span>)}
        </div>
      ))}
    </div>
  );
};

const sessionLaps = (s) => (Number(s.laps) || 0) + (s.pit ? Number(s.laps2) || 0 : 0);

function groupByDriver(sessions, drivers = []) {
  const map = new Map();
  sessions.forEach((s) => {
    const k = s.driver || "";
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(s);
  });
  const rank = (k) => (k === "" ? 1e6 : drivers.includes(k) ? drivers.indexOf(k) : 1e5);
  return [...map.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([driver, items]) => {
      const best = items.reduce((acc, s) => {
        const sec = sessionBestSeconds(s);
        if (!sec || (acc && acc.sec <= sec)) return acc;
        const lap = [s.best_lap, s.pit ? s.best_lap2 : null].find((l) => lapToSeconds(l) === sec);
        return { sec, lap };
      }, null);
      return { driver, items, best: best?.lap, laps: items.reduce((n, s) => n + sessionLaps(s), 0) };
    });
}

const LapTotal = ({ sessions, drivers }) => {
  const { t } = useT();
  const groups = groupByDriver(sessions, drivers);
  const total = groups.reduce((n, g) => n + g.laps, 0);
  return (
    <div data-testid="event-total-laps" className="border border-[#FFB800]/50 bg-[#FFB800]/10 rounded-sm px-4 py-3 min-w-[260px]">
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB800]/80">{t("laps_per_driver")}</span>
        <span data-testid="event-total-laps-value" className="font-mono text-xs text-slate-400">{t("total")}: <b className="text-white">{total}</b></span>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {groups.length === 0 && <span className="font-heading font-extrabold text-5xl leading-none text-[#FFB800]">0</span>}
        {groups.map((g) => (
          <div key={g.driver || "_none"} data-testid={`event-driver-laps-${g.driver || "none"}`}>
            <div className="font-heading font-extrabold text-5xl leading-none text-[#FFB800] tabular-nums">{g.laps}</div>
            <div className="font-mono text-[11px] uppercase text-slate-300 mt-1 max-w-[140px] truncate">{g.driver || t("no_driver")}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EventHeader = ({ event, sessions, onEdit, onDelete }) => {
  const { t, lang } = useT();
  return (
    <div className="fade-up mb-10">
      <Link to="/events" data-testid="back-to-events-link" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-slate-500 hover:text-white mb-5 transition-colors duration-200">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("nav_events")}
      </Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
        <div>
          <span data-testid="event-detail-type" className="font-mono text-xs uppercase tracking-wider text-[#FF3B30]">
            {t(`type_${event.event_type}`)}{event.country ? ` · ${event.country}` : ""}
          </span>
          <h1 data-testid="event-detail-name" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-2">{event.name}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-slate-400 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /><span data-testid="event-detail-track">{event.track}</span></span>
            <span className="flex items-center gap-1.5 font-mono"><CalendarDays className="w-4 h-4" />
              {formatDate(event.start_date, lang)}{event.end_date && event.end_date !== event.start_date ? ` → ${formatDate(event.end_date, lang)}` : ""}
            </span>
          </div>
          <EventLists event={event} />
          {event.notes && <p className="text-slate-400 mt-3 max-w-2xl text-sm whitespace-pre-line">{event.notes}</p>}
        </div>
        <div className="flex flex-col items-start lg:items-end gap-3">
          <LapTotal sessions={sessions} drivers={event.drivers} />
        <div className="flex gap-2">
          <Button variant="outline" data-testid="edit-event-btn" onClick={onEdit} className="rounded-sm bg-transparent border-[#282D3D] hover:bg-[#181B26] hover:text-white">
            <Pencil className="w-4 h-4 mr-1.5" /> {t("edit")}
          </Button>
          <ConfirmDelete testid="delete-event" description={t("confirm_event")} onConfirm={onDelete} trigger={
            <Button variant="outline" data-testid="delete-event-btn" className="rounded-sm bg-transparent border-[#282D3D] text-slate-400 hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] hover:border-[#FF3B30]">
              <Trash2 className="w-4 h-4" />
            </Button>
          } />
        </div>
        </div>
      </div>
    </div>
  );
};

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const { garage } = useGarage();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [tyreSets, setTyreSets] = useState([]);

  const loadSets = useCallback(() => {
    api.get("/tyre-sets").then((r) => setTyreSets(r.data));
  }, []);

  useEffect(() => {
    loadSets();
  }, [loadSets]);

  const load = useCallback(async () => {
    try {
      const [ev, ss] = await Promise.all([api.get(`/events/${eventId}`), api.get(`/events/${eventId}/sessions`)]);
      setEvent(ev.data);
      setSessions(ss.data);
    } catch (e) {
      navigate("/events");
    }
  }, [eventId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const bestId = useMemo(() => {
    let best = null;
    sessions.forEach((s) => {
      const sec = sessionBestSeconds(s);
      if (sec && (!best || sec < best.sec)) best = { id: s.id, sec };
    });
    return best?.id;
  }, [sessions]);

  const deleteEvent = async () => {
    await api.delete(`/events/${eventId}`);
    toast.success(t("deleted"));
    navigate("/events");
  };

  const deleteSession = async (s) => {
    await api.delete(`/sessions/${s.id}`);
    toast.success(t("deleted"));
    load();
  };

  const duplicate = (s) => navigate(`/events/${eventId}/sessions/new`, { state: { copyFrom: s } });

  if (!event) return <div data-testid="event-loading" className="font-mono text-xs text-slate-500">{t("loading")}</div>;

  return (
    <div>
      <EventHeader event={event} sessions={sessions} onEdit={() => setEditOpen(true)} onDelete={deleteEvent} />
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-bold uppercase text-2xl sm:text-3xl tracking-tight">
          {t("sessions")} <span data-testid="sessions-count" className="font-mono text-lg text-slate-500">[{sessions.length}]</span>
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="quick-import-btn" onClick={() => setQuickOpen(true)}
            className="rounded-sm bg-transparent border-[#FFB800]/50 text-[#FFB800] hover:bg-[#FFB800]/10 hover:text-[#FFB800] font-heading uppercase tracking-wide text-base h-10 px-4">
            <Zap className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">{t("qi_btn")}</span>
          </Button>
          <Button data-testid="new-session-btn" onClick={() => navigate(`/events/${eventId}/sessions/new`)}
            className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base h-10 px-4 transition-transform duration-150 active:scale-95">
            <Plus className="w-4 h-4 mr-1" /> {t("new_session")}
          </Button>
        </div>
      </div>
      {sessions.length === 0 ? (
        <div data-testid="sessions-empty" className="border border-dashed border-[#282D3D] rounded-sm p-12 text-center text-slate-500 font-mono text-sm">{t("no_sessions")}</div>
      ) : (
        <div data-testid="sessions-groups" className="space-y-10">
          {groupByDriver(sessions, event.drivers).map((g) => (
            <section key={g.driver || "_none"} data-testid={`driver-group-${g.driver || "none"}`}>
              <div className="flex items-baseline gap-3 mb-4 border-b border-[#282D3D] pb-2">
                <span className="w-1 h-5 bg-[#FF3B30] self-center" />
                <h3 data-testid="driver-group-name" className="font-heading font-bold uppercase text-xl sm:text-2xl">{g.driver || t("no_driver")}</h3>
                <span className="font-mono text-xs text-slate-500">[{g.items.length}]</span>
                <span data-testid="driver-group-laps" className="self-center font-heading font-bold text-lg leading-none px-2 py-1 rounded-sm bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800]">{g.laps} {t("total_laps")}</span>
                {g.best && <span data-testid="driver-group-best" className="ml-auto font-mono text-sm text-[#10B981]">{t("best_lap")}: <b>{g.best}</b></span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {g.items.map((s, i) => (
                  <SessionCard key={s.id} index={i} session={s} eventId={eventId} isBest={s.id === bestId} garage={garage} tyreSets={tyreSets} onDuplicate={duplicate} onDelete={deleteSession} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <EventTyreSets eventId={eventId} sets={tyreSets.filter((s) => s.event_id === eventId)} onChange={loadSets} />
      <EventFormDialog open={editOpen} onOpenChange={setEditOpen} event={event} onSaved={setEvent} />
      <QuickImportDialog open={quickOpen} onOpenChange={setQuickOpen} event={event} garage={garage} onDone={load} />
    </div>
  );
}
